import React, { useState, useEffect, memo, useRef } from 'react'
import { Toaster, toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import NewModelConfigurationForm from '@/components/dashboard/newModelConfigurationForm'
import { useUser } from '@/context/UserContext'
import {
  useGetModelConfigs,
  useUpdateModelConfig,
  useDeleteModelConfig,
} from '@/stores/modelsStore'

function ModelManager() {
  const { configs, configsLoading, fetchConfigs } = useGetModelConfigs()
  const updateModelConfig = useUpdateModelConfig()
  const deleteModelConfig = useDeleteModelConfig()
  const user = useUser()

  const [isModalOpen, setModalOpen] = useState(false)
  const [expandedConfigs, setExpandedConfigs] = useState<Record<string, boolean>>({})
  const [isEnabled, setIsEnabled] = useState<Record<string, boolean>>({})
  const [formValues, setFormValues] = useState<Record<string, any>>({})

  const toggleModal = () => {
    setModalOpen(!isModalOpen)
  }

  useEffect(() => {
    if (!isModalOpen) {
      fetchConfigs()
    }
  }, [isModalOpen])

  const toggleExpand = (id: string) => {
    setExpandedConfigs((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }))
  }

  const showToast = (msg: string, severity = 'success') => {
    if (severity === 'success') {
      toast.success(msg)
    } else {
      toast.error(msg)
    }
  }

  const handleInputChange = (
    id: string,
    e: { target: { name: string; value: string | number } }
  ) => {
    const { name, value } = e.target
    setFormValues((prevState) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        [name]: value,
      },
    }))
  }

  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    setIsEnabled((prevState) => ({
      ...prevState,
      [id]: isChecked,
    }))
  }

  const isFormValid = (config: any) => {
    const values = formValues[config.id] || {}
    return (
      values.name &&
      values.config &&
      values.type &&
      values.identifier &&
      values.availableCapabilities &&
      values.pricing &&
      values.TopP !== undefined &&
      values.Temperature !== undefined &&
      values.MaxTokens !== undefined
    )
  }

  useEffect(() => {
    fetchConfigs()
  }, [fetchConfigs])

  useEffect(() => {
    const initialFormValues = {}
    const initialIsEnabled: Record<string, boolean> = {}
    configs?.forEach((config) => {
      initialFormValues[config.id] = {
        name: config.name,
        config: JSON.stringify(config.config),
        type: config.type,
        identifier: config.identifier,
        availableCapabilities: config.availableCapabilities.join(','),
        pricing: JSON.stringify(config.pricing, null, 2),
        TopP: config.TopP,
        Temperature: config.Temperature,
        MaxTokens: config.MaxTokens,
      }
      initialIsEnabled[config.id] = config.isEnabled
    })
    setFormValues(initialFormValues)
    setIsEnabled(initialIsEnabled)
  }, [configs])

  const handleSubmit = (id: string) => {
    let updatedConfig
    try {
      updatedConfig = {
        ...formValues[id],
        isEnabled: isEnabled[id],
        availableCapabilities: formValues[id].availableCapabilities
          .split(',')
          .map((cap) => cap.trim()),
        config: JSON.parse(formValues[id].config),
        pricing: JSON.parse(formValues[id].pricing),
        TopP: parseFloat(formValues[id].TopP),
        Temperature: parseFloat(formValues[id].Temperature),
        MaxTokens: parseInt(formValues[id].MaxTokens, 10),
      }
      updateModelConfig.execute({
        id,
        data: { ...updatedConfig },
        onSuccess: () => {
          showToast('Updated model successfully')
          fetchConfigs()
        },
        onError: (error) => {
          showToast('Error updating model config', 'error')
        },
      })
    } catch (error) {
      showToast('Error updating configuration: ' + error.toString(), 'error')
      // Handle the error appropriately, e.g., show an error message to the user
      // You might want to set updatedConfig to a default value or the original formValues[id]
      updatedConfig = formValues[id]
    }
    console.log('Updating model config:', updatedConfig)
  }

  const handleDelete = async (id) => {
    confirmDialog({
      message: 'Are you sure you want to delete this model?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await deleteModelConfig.execute({
          id: id,
          onSuccess: (response) => {
            showToast('Model deleted successfully')
            fetchConfigs()
          },
          onError: (error) => {
            showToast('Error deleting model' + error.toString(), 'error')
            console.error('Error deleting configuration:', error)
          },
        })
      },
    })
  }

  if (configsLoading) {
    return <div>Loading...</div>
  }

  return (
    <div
      style={{
        padding: '20px',
        maxWidth: '800px',
        margin: 'auto',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <h1 style={{ textAlign: 'center', color: '#333' }}>
        Model Configurations
      </h1>
      <h2 style={{ color: '#555' }}>
        User: {user.name} ({user.email})
      </h2>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Button onClick={toggleModal} icon="pi pi-plus">
          <style>{`.pi.pi-plus { margin-right: 10px; }`}</style>
          New Configuration
        </Button>
      </div>
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>New Model Configuration</DialogTitle>
          </DialogHeader>
          <NewModelConfigurationForm closeModal={() => setModalOpen(false)} />
        </DialogContent>
      </Dialog>
      <h3 style={{ color: '#666' }}>List of available configurations</h3>
      {configs?.map((config) => (
        <div
          key={config.id}
          style={{
            border: '1px solid #007BFF',
            padding: '10px',
            marginBottom: '10px',
            borderRadius: '5px',
            backgroundColor: '#f8f9fa',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <h4 style={{ margin: 0 }}>{config.name}</h4>
              <p style={{ margin: 0 }}>
                <strong>Type:</strong> {config.type}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Identifier:</strong> {config.identifier}
              </p>
            </div>
            <Button
              onClick={() => toggleExpand(config.id)}
              label={
                expandedConfigs[config.id] ? 'Hide Details' : 'Show Details'
              }
              icon={
                expandedConfigs[config.id]
                  ? 'pi pi-angle-up'
                  : 'pi pi-angle-down'
              }
              style={{ cursor: 'pointer', marginLeft: '10px' }}
            />
          </div>
          {expandedConfigs[config.id] && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Name
                  <Input
                    name="name"
                    value={formValues[config.id]?.name}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Type
                  <Input
                    name="type"
                    value={formValues[config.id]?.type}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Identifier
                  <Input
                    name="identifier"
                    value={formValues[config.id]?.identifier}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Available Capabilities (comma separated)
                  <Input
                    name="availableCapabilities"
                    value={formValues[config.id]?.availableCapabilities}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Config (JSON)
                  <Textarea
                    name="config"
                    value={formValues[config.id]?.config}
                    rows={5}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Pricing (JSON)
                  <Textarea
                    name="pricing"
                    value={formValues[config.id]?.pricing}
                    rows={5}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  TopP
                  <Input
                    name="TopP"
                    type="number"
                    value={formValues[config.id]?.TopP}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Temperature
                  <Input
                    name="Temperature"
                    type="number"
                    value={formValues[config.id]?.Temperature}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <Label>
                  Max Tokens
                  <Input
                    name="MaxTokens"
                    type="number"
                    value={formValues[config.id]?.MaxTokens}
                    style={{ width: '100%' }}
                    onChange={(e) => handleInputChange(config.id, e)}
                  />
                </Label>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-default"
                    checked={isEnabled[config.id]}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(config.id, checked)
                    }
                  />
                  <Label htmlFor="is-default">Set as default</Label>
                </div>
              </div>
              <Button
                onClick={() => handleSubmit(config.id)}
                label="Update"
                icon="pi pi-check"
                disabled={!isFormValid(config)}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      the model configuration.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(config.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      ))}
      <Toaster />
    </div>
  )
}

export default memo(ModelManager)
